import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, storage } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => storage.getUser());
  const [token, setToken] = useState(() => storage.getToken());
  const [builderScope, setBuilderScopeState] = useState(() => storage.getBuilderScope());
  const [loading, setLoading] = useState(Boolean(storage.getToken()));

  const isAdmin = user?.role === "admin";
  const isBuilder = user?.role === "builder";

  const applySession = useCallback((nextToken, nextUser) => {
    storage.setToken(nextToken);
    storage.setUser(nextUser);
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const logout = useCallback(() => {
    storage.clear();
    setToken("");
    setUser(null);
    setBuilderScopeState("");
  }, []);

  const setBuilderScope = useCallback((id) => {
    storage.setBuilderScope(id);
    setBuilderScopeState(id);
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    api("/auth/me")
      .then((res) => {
        if (cancelled) return;
        storage.setUser(res.data);
        setUser(res.data);
      })
      .catch(() => {
        if (!cancelled) logout();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  const login = useCallback(
    async ({ email, password }) => {
      const res = await api("/auth/login", { method: "POST", body: { email, password } });
      applySession(res.data.token, res.data.user);
      return res.data.user;
    },
    [applySession]
  );

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAdmin,
      isBuilder,
      builderScope,
      setBuilderScope,
      login,
      logout,
    }),
    [user, token, loading, isAdmin, isBuilder, builderScope, setBuilderScope, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
